import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import ProfileImage from "../profileImage";

const UserSidebar = ({ userProfile, followStats }) => {
  const { t } = useTranslation();
  
  if (!userProfile) {
    return (
      <div className="animate-pulse">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
          <div className="space-y-2 text-center">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-3 bg-gray-200 rounded w-16"></div>
          </div>
          <div className="flex space-x-4">
            <div className="text-center">
              <div className="h-6 bg-gray-200 rounded w-8"></div>
              <div className="h-3 bg-gray-200 rounded w-12 mt-1"></div>
            </div>
            <div className="text-center">
              <div className="h-6 bg-gray-200 rounded w-8"></div>
              <div className="h-3 bg-gray-200 rounded w-12 mt-1"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      {/* Enhanced Profile Picture */}
      <Link
        to={`/feed/${userProfile?._id || userProfile?.id}`}
        className="block group"
      >
        <div className="relative inline-block">
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#E05720] to-[#E05720]/80 p-0.5 transition-transform duration-300 group-hover:scale-105">
            <ProfileImage
              image={userProfile?.profilePicture}
              alt={`${userProfile?.firstName} ${userProfile?.lastName}`}
              className="w-full h-full rounded-full object-cover bg-white"
              user={userProfile}
            />
          </div>
        </div>
      </Link>

      {/* Enhanced User Info */}
      <div className="mt-4 space-y-2">
        <Link
          to={`/feed/${userProfile?._id || userProfile?.id}`}
          className="block group"
        >
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#E05720] transition-colors duration-200">
            {userProfile?.firstName} {userProfile?.lastName}
          </h3>
        </Link>

        <Link
          to={`/feed/${userProfile?._id || userProfile?.id}`}
          className="inline-flex items-center text-gray-600 hover:text-[#E05720] transition-colors duration-200 text-sm"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
            />
          </svg>
          {userProfile?.userName}
        </Link>

        {/* Account Type Badge */}
        {userProfile?.accountType && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E05720]/10 text-[#E05720] capitalize">
              <svg
                className="w-3 h-3 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {userProfile.accountType}
            </span>
          </div>
        )}
      </div>

      {/* Enhanced Stats */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#E05720]/5 to-[#E05720]/10 rounded-xl p-3 transition-transform duration-200 hover:scale-105">
          <div className="text-xl font-bold text-[#E05720]">
            {followStats?.followersCount || 0}
          </div>
          <div className="text-xs text-[#E05720]/80 font-medium">{t("feed.followers")}</div>
        </div>
        <div className="bg-gradient-to-br from-[#E05720]/5 to-[#E05720]/10 rounded-xl p-3 transition-transform duration-200 hover:scale-105">
          <div className="text-xl font-bold text-[#E05720]">
            {followStats?.followingCount || 0}
          </div>
          <div className="text-xs text-[#E05720]/80 font-medium">{t("feed.following")}</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 space-y-2">
        <Link
          to={`/feed/${userProfile?._id || userProfile?.id}`}
          className="w-full inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-[#E05720] to-[#E05720]/80 text-white text-sm font-medium rounded-lg hover:from-[#E05720]/90 hover:to-[#E05720]/70 transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          {t("feed.viewProfile")}
        </Link>
      </div>
    </div>
  );
};

export default UserSidebar;
